/**
 * Immagine remota servita tramite l'ottimizzatore di Vercel.
 *
 * Il feed espone i JPEG originali (media ~600 KB, picchi oltre 1,5 MB) e il
 * server sorgente non accetta parametri di resize ne' negozia WebP: l'unico
 * modo di ridurli e' farli passare da /_vercel/image, che li riconverte e li
 * ridimensiona.
 *
 * Serve la chiave `images` in vercel.json: finche' manca, Vercel non espone
 * l'endpoint e il path finisce nel rewrite SPA rispondendo 200 text/html.
 * Non serve invece escludere /_vercel dal rewrite: con `images` presente
 * l'endpoint viene risolto prima (verificato su Koopernew, stesso account,
 * che ottimizza le stesse immagini senza alcuna esclusione).
 *
 * Sia le larghezze sia la qualita' richieste devono essere dichiarate in
 * `images.sizes` e `images.qualities`, altrimenti Vercel rifiuta la richiesta.
 */

/** Deve combaciare con `images.sizes` in vercel.json. */
const WIDTHS = [320, 480, 640, 960, 1280, 1920];

/**
 * Deve combaciare con `images.remotePatterns` in vercel.json. Un host non in
 * questa lista non e' un errore da correggere qui: e' Vercel che rifiuta con
 * 400 chi non e' dichiarato, quindi va servito come URL originale. Successo
 * reale: un articolo con `a.image` su Unsplash (dato del backend, non un
 * asset nostro) e' passato di qui e ha preso 400 finche' la lista era vuota.
 */
const ALLOWED_HOSTS = ['www.galileospa.com'];

/**
 * Un path locale (frontend/public/...) e' sempre ottimizzabile: e' lo stesso
 * dominio del deploy, Vercel non richiede remotePatterns per la propria
 * origine, esattamente come next/image non lo richiede per gli asset in /public.
 */
const isLocalPath = (src) => typeof src === 'string' && src.startsWith('/');

/** L'ottimizzatore esiste solo su Vercel, e solo per gli host dichiarati (o per i nostri asset locali). */
const optimizable = (src) => {
  if (process.env.NODE_ENV !== 'production' || typeof src !== 'string') return false;
  if (isLocalPath(src)) return true;
  try {
    return ALLOWED_HOSTS.includes(new URL(src).hostname);
  } catch {
    return false;
  }
};

export const optimized = (src, w = 640) =>
  optimizable(src) ? `/_vercel/image?url=${encodeURIComponent(src)}&w=${w}&q=75` : src;

/**
 * Nota sui default: `sizes` descrive la griglia prodotti, che e' il caso d'uso
 * piu' frequente. Le immagini a piena larghezza devono passare sizes="100vw",
 * altrimenti il browser sceglie un file troppo piccolo.
 */
export const Img = ({
  src,
  alt = '',
  sizes = '(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw',
  width = 640,
  height,
  aspectRatio = 1,
  priority = false,
  onError,
  ...rest
}) => {
  if (!src) return null;
  const remote = optimizable(src);

  /**
   * Un'immagine con onError che fallisce NON deve restare lazy: con
   * `loading="lazy"` il browser ri-tenta il caricamento a ogni rivalutazione
   * dell'IntersectionObserver, e se l'onError nasconde l'elemento
   * (`display: none`) il retry non si ferma mai — misurato ~500 richieste/s
   * verso l'ottimizzatore su un asset locale inesistente (ambient.webp).
   * Con `eager` la richiesta parte una volta sola, l'onError nasconde
   * l'immagine e il browser non riprova.
   */
  const hasErrorHandler = typeof onError === 'function';

  /**
   * Il box va riservato sempre: un'immagine senza width/height fa scorrere il
   * contenuto sotto quando arriva, ed era la causa di un CLS di 0,39 a cache
   * fredda. `height` esplicito vince; altrimenti si deriva da aspectRatio, che
   * per default e' il quadrato: proporzione imperfetta ma box comunque riservato.
   */
  const renderedHeight = height ?? Math.round(width / aspectRatio);

  /**
   * Finche' `srcset` e' popolato il browser sceglie da li' e ignora un nuovo
   * `src` assegnato a mano: senza rimuoverlo, gli onError dei chiamanti che
   * fanno `e.currentTarget.src = FALLBACK` non mostrerebbero mai il fallback,
   * e continuerebbero a riprovare candidati tutti rotti.
   *
   * Gli attributi vanno RIMOSSI, non azzerati a stringa vuota: assegnare
   * `srcset = ''` cambia l'attributo e Chromium riavvia la valutazione dei
   * candidati, ripartendo la richiesta — misurato ~600 req/s verso un asset
   * locale inesistente (ambient.webp) finche' l'elemento resta nel DOM.
   * Rimuovere anche `src`: chi non assegna un fallback si ferma del tutto,
   * chi lo assegna (src = FALLBACK) riparte pulito.
   */
  const handleError = (e) => {
    e.currentTarget.removeAttribute('srcset');
    e.currentTarget.removeAttribute('sizes');
    e.currentTarget.removeAttribute('src');
    onError?.(e);
  };

  return (
    <img
      src={optimized(src, width)}
      srcSet={remote ? WIDTHS.map((w) => `${optimized(src, w)} ${w}w`).join(', ') : undefined}
      sizes={remote ? sizes : undefined}
      alt={alt}
      width={width}
      height={renderedHeight}
      loading={priority || hasErrorHandler ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : undefined}
      decoding="async"
      onError={handleError}
      {...rest}
    />
  );
};
